import { addNodesToTheGroup } from "@features/builder/graph/changeGroup/moveToGroup"
import { findMaxId } from "@features/builder/graph/helpers"
import { getIntersectingGroup } from "@features/builder/graph/intersection"
import {
  AppNode,
  DeterministicNode,
  EdgeData,
  GroupNode,
  LagNode,
  NodeType,
} from "@features/builder/graph/types"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { removeEdgeFromNode } from "@features/builder/helpers/remove-edge"
import { getParentsList } from "@utils/node/getParentsList"
import { Edge, InternalNode, ReactFlowInstance } from "@xyflow/react"
import { XYPosition, nodeToRect } from "@xyflow/system"

type OldToNewMap = Map<string, string>

/**
 * We want to move nodes to the new group only when GROUP is duplicated (not just selected)
 * When just node(s) are duplicated, they need to stay in the same group.
 * Keep in mind, that group is a node and will stay in it's parent group as normal node would do.
 * It will move to new group if it was part of other duplicated group (if it was sub-group).
 * If Group is duplicated without selecting it (direct right click), we select all it's descendants,
 * and handle it like normal selection
 *
 */
export function duplicateNodes({
  allSourceNodes,
  allTargetNodes,
  underCursorNodeId,
  position,
  reactFlowInstance,
}: {
  /** all incoming nodes from a graph that copy was initialized. Used to select nodes for copy */
  allSourceNodes: InternalNode<AppNode>[]
  /** all nodes from current graph on the time of paste is performed. Used to determine new MaxId when pasting */
  allTargetNodes: AppNode[]
  /** node id that may be unselected but should be added regardless of its selection state */
  underCursorNodeId?: string
  /** reactflow coordinates where new duplicated nodes should be placed */
  position?: XYPosition
  /** reactflow instance that allows us doing possibly needed calculations */
  reactFlowInstance: ReactFlowInstance<AppNode, Edge<EdgeData>>
}): { nodes: AppNode[]; oldToNewIdMap: OldToNewMap } {
  const updatedNodes = structuredClone(allSourceNodes)
  const duplicatedNodesInternal: InternalNode<AppNode>[] = []
  const oldToNewIdMap: OldToNewMap = new Map() // use it to update node.parentId

  const mapper = nodesGroupMapper(updatedNodes)
  const subGroupIds: string[] = []
  const subNodeIds: string[] = []
  const selectedNodeIds = allSourceNodes.filter((node) => node.selected).map((node) => node.id)

  for (const node of allSourceNodes) {
    if (node.id === underCursorNodeId) node.selected = true
    if (!node.selected) continue

    subGroupIds.push(...(mapper.parentToChildGroupsIdsMapDeep.get(node.id) ?? []))
    subNodeIds.push(...(mapper.groupToNodesIdsMapDeep.get(node.id) ?? []))
  }

  const allNodesToSelect = new Set([...subNodeIds, ...subGroupIds, ...selectedNodeIds])

  // calculate distances between nodes to keep them when pasting duplicated nodes at different position
  const offsetsMap = getNodesOffsets(updatedNodes, allNodesToSelect)

  for (const internalNode of updatedNodes) {
    const node = internalNode.internals.userNode

    if (allNodesToSelect.has(node.id)) {
      node.selected = true

      // if we are duplicating node to specific position, we want to detach copy from its original parent
      const isParentCopied = node.parentId && allNodesToSelect.has(node.parentId)
      if (position && !isParentCopied) {
        node.parentId = undefined
        if ("metaNode" in node.data) node.data.metaNode.groups = []
      }
    }
    if (!node.selected) continue

    // recalculate position with keeping distances between duplicated nodes
    if (node.id === underCursorNodeId || !node.parentId) {
      const newPosition = calculateDuplicatedNodePosition(internalNode, offsetsMap, position)
      node.position = newPosition
      internalNode.position = newPosition
      internalNode.internals.positionAbsolute = newPosition
    }

    const allNodes = [
      ...allTargetNodes,
      ...duplicatedNodesInternal.map((n) => n.internals.userNode),
    ]

    const newId = findMaxId(allNodes, node.type === NodeType.Group)
    oldToNewIdMap.set(node.id, newId)

    // Update id/name (it's a same thing, we use both)
    internalNode.id = newId
    node.id = newId
    if (
      node.type === NodeType.Table ||
      node.type === NodeType.ScenarioTable ||
      node.type === NodeType.ForecastTable
    ) {
      node.data.originalAttribute.name = node.id
    } else {
      if (node.type !== NodeType.Group) {
        node.data.originalNode.name = node.id
      }
    }

    // Select all new nodes, so user can drag them to desired position
    node.selected = true

    duplicatedNodesInternal.push(internalNode)
  }

  const duplicatedNodes = duplicatedNodesInternal.map((n) => n.internals.userNode)
  if (!position) {
    return {
      nodes: updateOldToNewId(duplicatedNodes, oldToNewIdMap, allSourceNodes),
      oldToNewIdMap,
    }
  }

  // if duplication is happening over a group, check if we should assign nodes to it
  const group = getIntersectingGroup(position, reactFlowInstance)
  const groupInternal = reactFlowInstance.getInternalNode(group?.id ?? "") as
    | InternalNode<GroupNode>
    | undefined

  if (!groupInternal || group?.data.metaGroup.isCollapsed) {
    return {
      nodes: updateOldToNewId(duplicatedNodes, oldToNewIdMap, allSourceNodes),
      oldToNewIdMap,
    }
  }

  const notGroupedDuplicatedNodes = duplicatedNodesInternal.filter(
    (node) => !node.internals.userNode.parentId,
  )
  const doAllNotParentedNodesFitIntoGroup = notGroupedDuplicatedNodes.every((internalNode) =>
    reactFlowInstance.isNodeIntersecting(groupInternal, nodeToRect(internalNode), true),
  )

  if (!doAllNotParentedNodesFitIntoGroup) {
    return {
      nodes: updateOldToNewId(duplicatedNodes, oldToNewIdMap, allSourceNodes),
      oldToNewIdMap,
    }
  }
  const allNodes = [...allTargetNodes, ...duplicatedNodesInternal.map((n) => n.internals.userNode)]
  const updatedNodesWithGroups = addNodesToTheGroup(
    groupInternal,
    notGroupedDuplicatedNodes,
    allNodes,
  )

  //  replace updated nodes in duplicatedNodesInternal
  for (const node of updatedNodesWithGroups) {
    const correspondingDuplicatedNode = duplicatedNodesInternal.find((n) => n.id === node.id)
    if (correspondingDuplicatedNode) correspondingDuplicatedNode.internals.userNode = node
  }

  return {
    nodes: updateOldToNewId(
      duplicatedNodesInternal.map((n) => n.internals.userNode),
      oldToNewIdMap,
      allSourceNodes,
    ),
    oldToNewIdMap,
  }
}

const getNodesOffsets = (allNodes: InternalNode<AppNode>[], nodeIdsToConsider: Set<string>) => {
  const offsetsMap = new Map<string, { x: number; y: number }>()
  const [firstNodeId] = nodeIdsToConsider
  const firstNode = allNodes.find((node) => node.id === firstNodeId)

  if (firstNode) {
    allNodes.forEach((node) => {
      if (!nodeIdsToConsider.has(node.id)) return

      offsetsMap.set(node.id, {
        x: node.internals.positionAbsolute.x - firstNode.internals.positionAbsolute.x,
        y: node.internals.positionAbsolute.y - firstNode.internals.positionAbsolute.y,
      })
    })
  }

  return offsetsMap
}

const calculateDuplicatedNodePosition = (
  node: InternalNode<AppNode>,
  offsetsMap: Map<string, { x: number; y: number }>,
  position: XYPosition | undefined,
): XYPosition => {
  // no new dedicated position, shift it slightly towards top and right
  if (!position) {
    return {
      x: node.position.x + 50,
      y: node.position.y + 50,
    }
  }

  const offset = offsetsMap.get(node.id)
  if (!offset) return node.position

  return {
    x: position.x + offset.x,
    y: position.y + offset.y,
  }
}

export function updateOldToNewId(
  collectedNodesToDuplicate: AppNode[],
  oldToNewIdMap: OldToNewMap,
  allSourceNodes: InternalNode<AppNode>[],
): AppNode[] {
  const updatedNodesWithGroups = updateDuplicatedNodesGroups(
    collectedNodesToDuplicate,
    oldToNewIdMap,
    allSourceNodes,
  )

  const updatedNodesWithEdges = updateDuplicatedNodesEdges(
    updatedNodesWithGroups,
    oldToNewIdMap,
    allSourceNodes,
  )

  return updatedNodesWithEdges
}

function updateDuplicatedNodesGroups(
  collectedNodesToDuplicate: AppNode[],
  oldToNewIdMap: OldToNewMap,
  allSourceNodes: InternalNode<AppNode>[],
) {
  const newToOldIdMap = createNewToOldIdMap(oldToNewIdMap)
  const nodesMap = new Map<AppNode["id"], AppNode>()
  const sourceParentsMap = new Map(
    allSourceNodes.map((node) => [node.id, node.internals.userNode.parentId]),
  )

  for (const node of collectedNodesToDuplicate) {
    let updatedNode = structuredClone(node)
    const originalNodeId = newToOldIdMap.get(node.id) ?? node.id
    const originalParentId = sourceParentsMap.get(originalNodeId)

    /**
     * When duplicating nodes, we want to update group that duplicated nodes are asssigned to depending if new group is also duplicated or not.
     * If group is duplicated, we want to assign duplicated nodes to new group, so we update parentId to new group id.
     */
    updatedNode = updatedDuplicatedNodeGroup(updatedNode, oldToNewIdMap, originalParentId)
    nodesMap.set(updatedNode.id, updatedNode)
  }

  const mapper = nodesGroupMapper([...nodesMap.values()])
  for (const node of nodesMap.values()) {
    const updatedNode = updateMetaNodeGroups(structuredClone(node), mapper)
    nodesMap.set(updatedNode.id, updatedNode)
  }

  return [...nodesMap.values()]
}

function updatedDuplicatedNodeGroup(
  node: AppNode,
  oldToNewIdMap: OldToNewMap,
  originalParentId?: string,
) {
  if (!node.parentId || node.parentId !== originalParentId) {
    return node
  }

  const newParentId = oldToNewIdMap.get(node.parentId)

  if (newParentId && newParentId !== node.id) {
    node.parentId = newParentId
    if (node.type === NodeType.Group) {
      node.data.metaGroup.parent = newParentId
    }
  }

  return node
}

function updateMetaNodeGroups(node: AppNode, mapper: ReturnType<typeof nodesGroupMapper>) {
  if (node.type === NodeType.Group) return node
  if (!node.parentId) {
    node.data.metaNode.groups = []
    return node
  }

  const ancestors = mapper.childToParentGroupsIdsMapDeep.get(node.parentId) ?? []

  node.data.metaNode.groups = [node.parentId, ...ancestors]

  return node
}

function updateDuplicatedNodesEdges(
  collectedNodesToDuplicate: AppNode[],
  oldToNewIdMap: OldToNewMap,
  allSourceNodes: InternalNode<AppNode>[],
): AppNode[] {
  const newToOldIdMap = createNewToOldIdMap(oldToNewIdMap)

  // First from original nodes we want to detach edges that are not going to be duplicated
  // because that may have impact on nodes that we actually want to duplicate
  const updatedSourceNodesMap = removeEdgesFromNonDuplicatedParents(
    collectedNodesToDuplicate,
    oldToNewIdMap,
    newToOldIdMap,
    allSourceNodes,
  )

  // Now we can apply changes we made to source nodes to duplicated nodes
  const updatedDuplicatedNodes = applyUpdatedSourceDataToDuplicatedNodes(
    collectedNodesToDuplicate,
    newToOldIdMap,
    updatedSourceNodesMap,
  )

  return updatedDuplicatedNodes.map((node) => updateNodeParentsIds(node, oldToNewIdMap))
}

/**
 * Create a reverse map from new node IDs to old node IDs
 */
function createNewToOldIdMap(oldToNewIdMap: OldToNewMap): Map<string, string> {
  const newToOldIdMap = new Map<string, string>()
  for (const [oldId, newId] of oldToNewIdMap) {
    newToOldIdMap.set(newId, oldId)
  }
  return newToOldIdMap
}

/**
 * Remove edges from source nodes to parents that are not being duplicated.
 * Returns a map of updated source nodes with edges removed and type conversions applied.
 */
function removeEdgesFromNonDuplicatedParents(
  collectedNodesToDuplicate: AppNode[],
  oldToNewIdMap: OldToNewMap,
  newToOldIdMap: Map<string, string>,
  allSourceNodes: InternalNode<AppNode>[],
): Map<AppNode["id"], AppNode> {
  const allUpdatedSourceNodesMap = new Map<AppNode["id"], AppNode>()
  allSourceNodes.forEach((internalNode) => {
    allUpdatedSourceNodesMap.set(internalNode.id, structuredClone(internalNode.internals.userNode))
  })

  for (const duplicatedNode of collectedNodesToDuplicate) {
    if (duplicatedNode.type === NodeType.Group) continue

    const oldNodeId = newToOldIdMap.get(duplicatedNode.id)
    if (!oldNodeId) continue

    const sourceNode = allUpdatedSourceNodesMap.get(oldNodeId)
    if (!sourceNode || sourceNode.type === NodeType.Group) continue

    const parentsList = getParentsList(sourceNode)
    const parentsNotDuplicated = parentsList.filter((parentId) => !oldToNewIdMap.has(parentId))

    for (const parentId of parentsNotDuplicated) {
      const parentNode = allUpdatedSourceNodesMap.get(parentId)
      if (!parentNode) continue

      const currentSourceNode = allUpdatedSourceNodesMap.get(oldNodeId)
      if (!currentSourceNode) continue

      const updatedNodes = removeEdgeFromNode(
        parentNode,
        currentSourceNode,
        Array.from(allUpdatedSourceNodesMap.values()),
      )
      updatedNodes.forEach((node) => allUpdatedSourceNodesMap.set(node.id, node))
    }
  }

  return allUpdatedSourceNodesMap
}

/**
 * Apply updated source node data (df_cpd, parents_lst, type conversions) to duplicated nodes.
 * Preserves duplicated node metadata (id, position, selection, parentId, groups).
 */
function applyUpdatedSourceDataToDuplicatedNodes(
  collectedNodesToDuplicate: AppNode[],
  newToOldIdMap: Map<string, string>,
  updatedSourceNodesMap: Map<string, AppNode>,
): AppNode[] {
  const updatedDuplicatedNodes: AppNode[] = []

  for (const duplicatedNode of collectedNodesToDuplicate) {
    if (duplicatedNode.type === NodeType.Group) {
      updatedDuplicatedNodes.push(duplicatedNode)
      continue
    }

    const oldNodeId = newToOldIdMap.get(duplicatedNode.id)
    if (!oldNodeId) {
      updatedDuplicatedNodes.push(duplicatedNode)
      continue
    }

    const updatedSourceNode = updatedSourceNodesMap.get(oldNodeId)
    if (!updatedSourceNode || updatedSourceNode.type === NodeType.Group) {
      updatedDuplicatedNodes.push(duplicatedNode)
      continue
    }

    // Copy the entire updated source node to duplicated node
    // This includes type conversion (e.g., mixture -> continuous) if it happened due to edge removal
    const updatedDuplicatedNode = structuredClone(updatedSourceNode) as AppNode

    // Preserve the duplicated node's id, position, selection state, and other metadata
    updatedDuplicatedNode.id = duplicatedNode.id
    updatedDuplicatedNode.position = duplicatedNode.position
    updatedDuplicatedNode.selected = duplicatedNode.selected
    updatedDuplicatedNode.parentId = duplicatedNode.parentId

    // Update the name in the data to match the new id
    if (
      updatedDuplicatedNode.type === NodeType.Table ||
      updatedDuplicatedNode.type === NodeType.ForecastTable ||
      updatedDuplicatedNode.type === NodeType.ScenarioTable
    ) {
      updatedDuplicatedNode.data.originalAttribute.name = duplicatedNode.id
    } else if (updatedDuplicatedNode.type !== NodeType.Group) {
      updatedDuplicatedNode.data.originalNode.name = duplicatedNode.id
    }

    // Preserve the metaNode groups from the duplicated node (already updated by updateDuplicatedNodesGroups)
    if (
      "metaNode" in duplicatedNode.data &&
      "metaNode" in updatedDuplicatedNode.data &&
      updatedDuplicatedNode.type !== NodeType.Group
    ) {
      updatedDuplicatedNode.data.metaNode = structuredClone(duplicatedNode.data.metaNode)
    }

    updatedDuplicatedNodes.push(updatedDuplicatedNode)
  }

  return updatedDuplicatedNodes
}

/**
 * Update node parents ids based on oldToNewIdMap in parents_lst and df_cpd
 */
function updateNodeParentsIds(node: AppNode, oldToNewIdMap: OldToNewMap) {
  if (node.type === NodeType.Group) {
    return node
  }

  if (
    node.type === NodeType.Table ||
    node.type === NodeType.ForecastTable ||
    node.type === NodeType.ScenarioTable
  ) {
    const updatedParentsLst = []
    for (const parent of node.data.originalAttribute.properties.parents_lst ?? []) {
      const newParent = oldToNewIdMap.get(parent)
      if (newParent) {
        // store new parents_lst
        updatedParentsLst.push(newParent)
      }
      // move old parent to new parent
      node.data.originalAttribute.properties.parents_lst = updatedParentsLst
    }
  } else {
    const updatedParentsLst = []
    // Any other node
    for (const parent of node.data.originalNode.properties.parents_lst) {
      const newParent = oldToNewIdMap.get(parent)
      if (newParent) {
        // store new parents_lst
        updatedParentsLst.push(newParent)

        // move old parent to new parent
        node.data.originalNode.properties.df_cpd[newParent] =
          node.data.originalNode.properties.df_cpd[parent]

        // clear old parent reference
        if (newParent !== parent) {
          delete node.data.originalNode.properties.df_cpd[parent]
        }
      }
    }
    node.data.originalNode.properties.parents_lst = updatedParentsLst
  }

  return node
}

export function updateNodeExpression(
  node: DeterministicNode | LagNode,
  oldToNewLabelMap: OldToNewMap,
) {
  const updatedNode = structuredClone(node)
  const expressions: string[] = []

  for (const expression of node.data.originalNode.properties.df_cpd.expression ?? []) {
    const mustachePattern = /\{([^}]+)\}/g
    const updatedExpression = expression.replace(mustachePattern, (match, nodeRef) => {
      const newId = oldToNewLabelMap.get(nodeRef)
      return newId ? `{${newId}}` : match
    })

    expressions.push(updatedExpression)
  }
  updatedNode.data.originalNode.properties.df_cpd.expression = expressions

  return updatedNode
}
